import { DropOverMenu } from "client/Components";
import { EmbedInputDialogData } from "client/Features/Hub/HubMediaSelect/EmbedInputDialog";
import { HubItemRowLayoutKind } from "client/Api/Hub/common";
import { localize } from "client_legacy/ndaw/culture";
import {
	forwardRef,
	ReactElement,
	Ref,
	RefObject,
	useCallback,
	useEffect,
	useImperativeHandle,
	useRef,
	useState,
	KeyboardEvent
} from "react";
import { rowLayoutToNumberOfColumnsMap } from "../Row";
import {
	ElementPicker
} from "./ElementPicker";
import { GridPicker } from "./GridPicker";
import styles from "./AddRowMenu.module.less";
import {
	MediaQueryKind,
	useWorkspaceMediaQuery
} from "client/Hooks";
import { Guid } from "client_legacy/ndaw/guid";
import {
	getClientId,
	ObjectWithClientId,
	setClientId
} from "client/Misc/clientId";
import {
	generateTrackingId,
	setTrackingId
} from "client/Misc/utils";
import { range } from "client_legacy/ndaw/arrayHelpers";
import { VideoInputDialogData } from "client/Features/Hub/HubMediaSelect/VideoInputDialog";
import {
	HubItemColumnFormModel,
	HubItemElementFormModel,
	HubItemRowFormModel
} from "client/Features/Hub/Management/common";
import {
	GridNavigationDirection,
	KeyboardGridNavigationContext,
	KeyboardGridNavigationHandlers,
	useKeyboardGridNavigation
} from "client/Hooks/useKeyboardGridNavigation";
import { Key, Rect } from "client/Misc/commonTypes";
import { useFocusTrap } from "client/Hooks/useFocusTrap";
import { never } from "client_legacy/ndaw/errors";
import { HubImageSelectorKind, ImageWithAttribution } from "client/Features/Hub/Management/useHubImageSelector";

export type AddRowMenuHandle = {
	focusFirstElement(): void;
};

export type AddRowMenuProps = {
	target: HTMLElement;
	onNewRow(
		row: ObjectWithClientId<HubItemRowFormModel>
	): void;
	onFindAndFocus(clientId: Guid): void;
	onClose(): void;
	onOpenVideoInputDialog(
		data: VideoInputDialogData
	): Promise<TryResult<string>>;
	onOpenEmbedInputDialog(
		data: EmbedInputDialogData
	): Promise<TryResult<string>>;
	trackingId: string;
	onSelectImage: (kind: HubImageSelectorKind) => Promise<TryResult<ImageWithAttribution>>;
	onFocusAddButton: () => void;
};

export const AddRowMenu = forwardRef(
	(props: AddRowMenuProps, ref: Ref<AddRowMenuHandle>): ReactElement => {
		const {
			target,
			onFindAndFocus,
			onNewRow,
			onClose,
			onOpenVideoInputDialog,
			onOpenEmbedInputDialog,
			onSelectImage,
			trackingId,
			onFocusAddButton
		} = props;

		const [lastFocusableIndex, setLastFocusableIndex] = useState<LastFocusableIndex>({
			kind: "elementPicker",
			index: 0
		});

		const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);
		const containerRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement | null>(null);

		const gridPickerNavigationRef: RefObject<KeyboardGridNavigationHandlers | null> = useRef<KeyboardGridNavigationHandlers | null>(null);

		useFocusTrap<HTMLDivElement>(containerRef);

		const elementPickerNavigation: KeyboardGridNavigationHandlers = useKeyboardGridNavigation({
			onFocusChange: (index: number): void => {
				setLastFocusableIndex({
					kind: "elementPicker",
					index
				});
			},
			onOutOfBounds: (direction: GridNavigationDirection, rect: Rect): void => {
				switch (direction) {
					case Key.ArrowDown:
					case Key.ArrowRight:
						gridPickerNavigationRef.current
							?.focusItemAtHandler({
								direction,
								rect
							});

						break;
					case Key.ArrowLeft:
					case Key.ArrowUp:
						// do nothing
						break;
					default:
						never(direction);
				}
			}
		});

		const gridPickerNavigation: KeyboardGridNavigationHandlers = useKeyboardGridNavigation({
			onFocusChange: (index: number): void => {
				setLastFocusableIndex({
					kind: "gridPicker",
					index
				});
			},
			onOutOfBounds: (direction: GridNavigationDirection, rect: Rect): void => {
				switch (direction) {
					case Key.ArrowUp:
					case Key.ArrowLeft:
						elementPickerNavigation.focusItemAtHandler({
							direction,
							rect
						});

						break;
					case Key.ArrowDown:
					case Key.ArrowRight:
						// do nothing
						break;
					default:
						never(direction);
				}
			}
		});

		useEffect(
			(): void => {
				gridPickerNavigationRef.current = gridPickerNavigation;
			},
			[elementPickerNavigation, gridPickerNavigation]
		);

		useImperativeHandle(
			ref,
			(): AddRowMenuHandle => ({
				focusFirstElement(): void {
					setLastFocusableIndex({
						kind: "elementPicker",
						index: 0
					});

					elementPickerNavigation.focusItemAtHandler("start");
				}
			}),
			[elementPickerNavigation]
		);

		const onKeyUpHandler = useCallback(
			(event: KeyboardEvent): void => {
				if (event.key === Key.Escape) {
					onClose();
					onFocusAddButton();
				}
			},
			[onClose, onFocusAddButton]
		);

		const selectElementHandler = useCallback(
			(element: HubItemElementFormModel): void => {
				const elementWithClientId: ObjectWithClientId<HubItemElementFormModel> = setClientId(element);
				const clientId: Guid = getClientId(elementWithClientId);

				onNewRow(
					setClientId({
						layout: HubItemRowLayoutKind.OneColumn,
						columns: [
							setClientId({
								elements: [
									elementWithClientId
								]
							})
						]
					})
				);

				setTimeout(
					() => onFindAndFocus(clientId),
					1
				);
			},
			[onFindAndFocus, onNewRow]
		);

		const selectGridHandler = useCallback(
			(kind: HubItemRowLayoutKind): void => {
				onNewRow(setClientId({
					layout: kind,
					columns: createColumns(rowLayoutToNumberOfColumnsMap[kind])
				}));
			},
			[onNewRow]
		);

		const onMenuItemsFocusReset = useCallback(
			(): void => {
				// We need to use setTimeout to ensure that the focus is reset after the new items are rendered
				setTimeout(
					() => {
						setLastFocusableIndex({
							kind: "elementPicker",
							index: 0
						});
						elementPickerNavigation.focusItemAtHandler("start");
					},
					0
				);
			},
			[elementPickerNavigation]
		);

		return (
			<DropOverMenu
				target={target}
				onClose={onClose}
				targetAlignment="bc"
				contentAlignment="tc"
				title={localize("_Add")}
				trackingId={generateTrackingId(trackingId, "__wrapper")}
				onKeyUp={onKeyUpHandler}
			>
				<div
					{...setTrackingId(trackingId)}
					ref={containerRef}
				>
					{isMobile
						? (
							<KeyboardGridNavigationContext.Provider value={elementPickerNavigation.contextData}>
								<ElementPicker
									onMenuItemsFocusReset={onMenuItemsFocusReset}
									onSelect={selectElementHandler}
									onOpenEmbedInputDialog={onOpenEmbedInputDialog}
									onOpenVideoInputDialog={onOpenVideoInputDialog}
									onSelectImage={onSelectImage}
									tabbableIndex={lastFocusableIndex.kind === "elementPicker"
										? lastFocusableIndex.index
										: undefined
									}
								/>
							</KeyboardGridNavigationContext.Provider>
						) : (
							<>
								<h4 className={styles.sectionTitle}>
									{localize("_Elements")}
								</h4>
								<KeyboardGridNavigationContext.Provider value={elementPickerNavigation.contextData}>
									<ElementPicker
										onMenuItemsFocusReset={onMenuItemsFocusReset}
										onSelect={selectElementHandler}
										onOpenEmbedInputDialog={onOpenEmbedInputDialog}
										onOpenVideoInputDialog={onOpenVideoInputDialog}
										onSelectImage={onSelectImage}
										tabbableIndex={lastFocusableIndex.kind === "elementPicker"
											? lastFocusableIndex.index
											: undefined
										}
									/>
								</KeyboardGridNavigationContext.Provider>
								<hr className={styles.sectionSeparator} />
								<h4 className={styles.sectionTitle}>
									{localize("_Grid")}
								</h4>
								<KeyboardGridNavigationContext.Provider value={gridPickerNavigation.contextData}>
									<GridPicker
										onSelect={selectGridHandler}
										tabbableIndex={lastFocusableIndex.kind === "gridPicker"
											? lastFocusableIndex.index
											: undefined
										}
									/>
								</KeyboardGridNavigationContext.Provider>
							</>
						)
					}
				</div>
			</DropOverMenu>
		);
	}
);
AddRowMenu.displayName = "AddRowMenu";

function createColumns(count: number): readonly ObjectWithClientId<HubItemColumnFormModel>[] {
	return range(count)
		.map(_ => setClientId(
			{
				elements: []
			}
		));
}

type LastFocusableIndex = {
	kind: "elementPicker" | "gridPicker";
	index: number;
};
